import { getApiClient } from '../services/api/index';
import { MEDIA_TYPE, MODEL_TYPE, RESOURCE_TYPE, UPLOAD_TYPE } from '../types/enums';

// export type RESOURCE_TYPE = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 11 | 12 | 13;
export type TUploadType = 0 | 1 | 2 | 3 | 4 | 5;
export type TPathPrefix =
  | 'project_template'
  | 'fonts'
  | 'images/map'
  | 'images/look'
  | 'pag-resource'
  | 'images/motion_style'
  | 'images/camera'
  | 'images/KA'
  | 'packaging'
  | 'platform_operation'
  | 'order'
  | 'TTS';

export interface OssCredential {
  access_key_id: string;
  access_key_secret: string;
  security_token: string;
  bucket: string;
  region: string;
  signed_url?: string;
  host?: string;
  signed_key?: string;
}

export interface IPrepareSimpleUploadResponse {
  upload_secret_id: string;
  upload_path: string;
  max_progress_percent: number; // 0 < max < 100
  callback_host: string;
  callback_url: string;
  credential: OssCredential;
  filename?: string;
  upload_full_name?: string;

  immobilization_prefix?: string;
  secret_str?: string;
  suffix?: string;
}

export interface IBizParams {
  resource_type: RESOURCE_TYPE;
  upload_type: TUploadType;
  model_type?: number;
  project_id?: number;
  path_prefix?: TPathPrefix;
}

export interface IPrepare extends IBizParams {
  filename: string;
  filesize: number; // 大小 byte
  auth_type: string;
}

// 准备上传（简单上传）
export const postPrepareSimpleUpload = async (data: {
  auth_type: string;
  filename: string;
  filesize: number;
  resource_type?: RESOURCE_TYPE;
  upload_type?: TUploadType;
}) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<IPrepareSimpleUploadResponse>(
    '/youyan/upload_media_v3/prepare_simple_upload/',
    data,
  );
  return res.data;
};

// 准备上传（完整上传）
export const postPrepareUpload = async (data: IPrepare) => {
  const apiClient = await getApiClient();

  const res = await apiClient.post<IPrepareSimpleUploadResponse>(
    '/youyan/upload_media_v3/prepare_upload/',
    data,
  );

  return res.data;
};

// 删除上传失败的文件
export const deleteUploadFile = async (data: { upload_secret_id: string }) => {
  const apiClient = await getApiClient();
  const res = await apiClient.delete<{}>('/youyan/upload_media_v3/', data);
  return res;
};

// 取消上传
export const cancelUpload = async (data: {
  upload_secret_id: string;
  failed_hint?: number;
  delete_data?: boolean;
}) => {
  const apiClient = await getApiClient();
  const res = await apiClient.post<void>('/youyan/upload_media_v3/cancel/', data);
  return res.data;
};

// 查询上传进度
export interface UploadStatusParams {
  resource_type?: number;
  upload_type?: number;
  media_type?: number;
  model_type?: number;
  upload_secret_id?: string;
}

export interface FailedInfo {
  reason: number;
  hint: number;
  detail: string;
}

export interface IMedia {
  account_id: number;
  children: IMedia[];
  cover_image_url: string;
  create_time: string;
  description: string;
  duration: number;
  duration_format: string;
  editable: boolean;
  end_edited_time: number;
  height: number;
  id: number;
  media_id?: number;
  is_modify: number;
  segment_node_id?: number;
  render_media_url?: string;
  metadata?: Record<string, unknown>;
  last_cover_image_url: string;
  media_type: MEDIA_TYPE;
  name: string;
  organization_id: number;
  path: string;
  size: number;
  size_format: string;
  start_edited_time: number;
  status: number;
  suffix: string;
  update_time: string;
  url: string;
  username: string;
  width: number;
  relatedObject: any;
  meta?: Record<string, unknown>;
  /** 知识库等场景：上传完成后用于构建列表项，对应 project_media 表 id */
  project_media_id?: number;
  file_url?: string;
}

export interface IUploadStatus {
  user_id: number;
  id: number;
  upload_secret_id: string;
  media_type: MEDIA_TYPE;
  upload_status: number;
  resource_type: number;
  update_time: string; // 修改为 string 类型，因为 API 返回的是 ISO 字符串
  model_type: MODEL_TYPE;
  upload_type: UPLOAD_TYPE;
  filename: string;
  failed_info: FailedInfo;
  create_time: string; // 修改为 string 类型，因为 API 返回的是 ISO 字符串
  filesize: number;
  media_id: number;
  eta: number; // 预计完成时间戳
  percent: number; // 处理进度百分比
  media?: IMedia;
  related_object?: unknown; // 相关对象信息
  project_media_id?: number;
}

export const getYouyanUploadMediaV3BatchStatus = async (params?: UploadStatusParams) => {
  const client = await getApiClient();
  const response = await client.get<IUploadStatus[]>(
    `/youyan/upload_media_v3/batch_status/`,
    params,
  );
  return response.data;
};

export const getYouyanUploadMediaV3Status = async (params?: UploadStatusParams) => {
  const client = await getApiClient();
  const response = await client.get<IUploadStatus>(`/youyan/upload_media_v3/status/`, params);
  return response.data;
};

// 查询单个上传状态
export const queryUploadStatus = async (params: { upload_secret_id: string }) => {
  const client = await getApiClient();
  const response = await client.get<IUploadStatus>('/youyan/upload_media_v3/status/', params);
  return response.data;
};

// 批量查询上传状态
export const batchQueryUploadStatus = async (params: {
  resource_type: RESOURCE_TYPE;
  upload_type: TUploadType;
}) => {
  const client = await getApiClient();
  const response = await client.get<IUploadStatus[]>(
    '/youyan/upload_media_v3/batch_status/',
    params,
  );
  return response.data;
};
